import * as React from 'react'
import {Box, Typography, Divider, TextField} from '@mui/material'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, withOpacity} from '../../../../styles/foundation/colors'

/**
 * Horizontal section strip for Weight Freeze (v2) — the same icon-tile
 * treatment as disputes-v2/TabBar.js (and weight/list/index.js's status
 * tabs), applied to seven items instead of five: a bordered white card,
 * tabs laid out in a row separated by vertical dividers, an accent
 * *bottom* border and a tinted background on the active tab.
 *
 * THE MODULE'S ONE TAB STRIP. It is used at two levels — the sub-module /
 * section rails in index.js and the four-view row inside the Initiate-freeze
 * dashboard — which is why `density` and the optional per-item `count` exist
 * below rather than a second component carrying a second look. See DENSITY.
 *
 * This used to be a vertical left-hand rail. That was a mistake: the
 * standalone mockup's rail read as a sidebar only because the mockup had no
 * application chrome around it. Inside the real Proship panel there is
 * already an app sidebar on the left, so a second vertical rail next to it
 * produced two side-by-side sidebars. The house pattern for "several
 * sections, one page" is TabBar.js's horizontal strip — this now follows
 * it, just wider (seven tabs) and, per repeated feedback, kept compact.
 *
 * Below the `sm` breakpoint the strip collapses to a native TextField
 * select — seven tabs squeezed onto a phone screen is worse than a select.
 * Both variants are always mounted (CSS-hidden, not conditionally
 * unmounted) so `focusActive` below can reach whichever one is visible.
 *
 * `disableSelectFallback` opts a caller OUT of that collapse: the icon-tile
 * row stays visible and horizontally scrollable at every width instead. Only
 * right for a row short enough to still make sense as a scroller on a phone —
 * the four-tab Initiate-freeze dashboard row, not this module's own seven-tab
 * rail. When set, the select is not rendered at all (rather than kept mounted
 * and CSS-hidden), so `focusActive` never has a hidden native control to fall
 * back to — and does not need one, since the tile row is then always
 * focusable directly.
 *
 * Exposes an imperative handle — `focusActive()` — rather than a plain ref
 * or DOM id, because "focus the active item" means something different in
 * each variant (a tab's Box vs a native <select>), and the caller (several
 * drawer-driven sections) shouldn't have to know which one is on screen.
 * When a drawer closes and its trigger button is gone, the caller does
 * `railRef.current.focusActive()` and never has to think about breakpoints.
 */
/**
 * Two densities of ONE strip, never two strips.
 *
 * `compact` is what this rail was shrunk to so seven section tabs fit a page
 * without costing a 74px row. `comfortable` restores the metrics
 * disputes-v2/TabBar.js:53-72 uses for a five-item row — 30px tile, 17px
 * icon, px 2 / py 1.25, minWidth 150, content centred, label weight constant
 * — which is what a four-item row can afford and what the merchant asked for.
 *
 * The split also does the hierarchy work. A page can stack a section rail and
 * a view row (the dev harness does), and two identically-sized strips read as
 * two peers competing for the same axis; the section rails' step-number
 * circles only separate them where steps exist, and the sub-module rail has
 * none. Size is the lever that always works.
 */
const DENSITY = {
  compact: {
    tile: 24, tileRadius: '7px', icon: 14, px: 1.25, py: 0.85, minWidth: 108, gap: 0.75,
    justify: 'flex-start', lineHeight: 1.3, dimInactiveLabel: true,
  },
  comfortable: {
    tile: 30, tileRadius: '8px', icon: 17, px: 2, py: 1.25, minWidth: 150, gap: 1,
    justify: 'center', lineHeight: 1.2, dimInactiveLabel: false,
  },
}

/**
 * An item's text. A `count` is appended INSIDE the label rather than rendered
 * as a sibling — mirroring disputes-v2/TabBar.js:74 — because it is part of
 * what the tab is called, so nothing can style, colour or wrap it away from
 * the words it belongs to. `typeof` rather than a truthiness test: a genuine
 * count of 0 has to render as "(0)", and an item with no count at all must
 * render no parentheses.
 */
function itemText(s) {
  return typeof s.count === 'number' ? `${s.label} (${s.count})` : s.label
}

const StepRail = React.forwardRef(function StepRail({
  sections, active, onChange, label = 'Weight Freeze sections', density = 'compact',
  disableSelectFallback = false,
}, ref) {
  const d = DENSITY[density] || DENSITY.compact
  const itemRefs = React.useRef({})
  const selectRef = React.useRef(null)

  React.useImperativeHandle(ref, () => ({
    focusActive() {
      const node = itemRefs.current[active]
      if (node) {
        node.focus()
        // A CSS-hidden tab (strip collapsed to the mobile select) can't
        // actually take focus — the browser silently no-ops the call above.
        // Only trust it if focus really landed there before skipping the
        // select fallback.
        if (document.activeElement === node) return
      }
      // With the select opted out of, there is no hidden native control to
      // reach for — the tile row above is never CSS-hidden, so it must have
      // taken focus already and this is unreached in practice. Still guarded
      // explicitly rather than relying on selectRef.current being null.
      if (!disableSelectFallback && selectRef.current) selectRef.current.focus()
    },
  }), [active, disableSelectFallback])

  const handleKeyDown = (event, id) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onChange(id)
    }
  }

  return (
    // minWidth: 0 — this root Box is a grid/flex ITEM wherever a caller mounts
    // it (both known callers do), and such items default to min-width:auto,
    // i.e. their content's min-content size. Without overriding that, the row
    // of tabs below (which does not wrap) would force the GRID CONTAINER to
    // grow to fit it, at every ancestor up to the page, instead of the inner
    // Box's own overflowX:auto ever getting a chance to engage. Overriding it
    // to 0 is what makes "the row scrolls inside itself" true regardless of
    // how narrow the caller's layout gets.
    <Box sx={{minWidth: 0}}>
      {/* xs: native select — real <select> so focusActive() above can
          reach it with a plain .focus() call, and so it opens the OS's own
          picker on a phone instead of a MUI popover eating the viewport.

          The doubled ampersand is load-bearing. This select is deliberately
          larger than the page's other inputs, and index.js's shell rule sizes
          every .MuiOutlinedInput-input down to SIZE['2xs'] from a selector of
          the SAME specificity (0,2,0) — with a single `&`, which of the two
          won would be decided by emotion's injection order, i.e. by luck.
          `&&` makes it (0,3,0) so the deliberate choice is the one that holds.

          Not rendered at all when `disableSelectFallback` is set — there is
          no breakpoint left where the tile row below is CSS-hidden, so this
          would be a select nothing ever shows, sitting in the DOM for free. */}
      {!disableSelectFallback && (
        <TextField
          select
          fullWidth
          size='small'
          value={active}
          onChange={(event) => onChange(event.target.value)}
          SelectProps={{native: true, inputRef: selectRef}}
          sx={{display: {xs: 'block', sm: 'none'}, bgcolor: WHITE, '&& .MuiInputBase-input': {fontFamily: FONT, fontSize: SIZE.sm}}}
        >
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.step ? `${s.step}. ${itemText(s)}` : itemText(s)}
            </option>
          ))}
        </TextField>
      )}

      {/* sm and up: the horizontal strip. `nav` + aria-label gives assistive
          tech a named landmark around what would otherwise be seven
          unlabelled buttons with no framing for how many there are or what
          they're for. The outer Box clips to the rounded corners; the inner
          Box is the actual scroll container — it must scroll horizontally
          rather than let tabs wrap or squash when the strip is narrower
          than seven tabs' natural width. The page body itself never gets a
          horizontal scrollbar because only this inner row opts into one.
          `disableSelectFallback` keeps this row visible at every width
          instead of only sm-and-up — the row itself already knows how to
          scroll horizontally, so nothing else here has to change. */}
      <Box
        component='nav'
        aria-label={label}
        sx={{
          display: disableSelectFallback ? 'block' : {xs: 'none', sm: 'block'},
          bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', overflow: 'hidden',
        }}
      >
        <Box sx={{display: 'flex', overflowX: 'auto'}}>
          {sections.map((s, i) => {
            const isActive = active === s.id
            const Icon = s.icon

            return (
              <React.Fragment key={s.id}>
                {i > 0 && <Divider orientation='vertical' flexItem sx={{borderColor: BORDER}} />}
                <Box
                  ref={(node) => { itemRefs.current[s.id] = node }}
                  role='button'
                  tabIndex={0}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => onChange(s.id)}
                  onKeyDown={(event) => handleKeyDown(event, s.id)}
                  sx={{
                    flex: '1 1 auto', minWidth: d.minWidth, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: d.justify, gap: d.gap,
                    px: d.px, py: d.py, cursor: 'pointer',
                    borderBottom: isActive ? `3px solid ${PRIMARY_BLUE}` : '3px solid transparent',
                    bgcolor: isActive ? withOpacity(PRIMARY_BLUE, 0.06) : 'transparent',
                    transition: 'all 0.15s',
                    '&:hover': {bgcolor: withOpacity(PRIMARY_BLUE, 0.04)},
                    '&:focus-visible': {outline: `2px solid ${PRIMARY_BLUE}`, outlineOffset: '-2px'},
                  }}
                >
                  {/* Step number as a small circle sitting on the icon
                      tile's corner — not an MUI Badge, which reserves its
                      own slack around the child and was the main driver of
                      the original rail rows' ~74px height. The circle's
                      4px overhang is absorbed by the tab's own padding, so
                      it adds no height to the row. */}
                  <Box sx={{position: 'relative', flexShrink: 0, width: d.tile, height: d.tile}}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: d.tile, height: d.tile, borderRadius: d.tileRadius,
                      bgcolor: isActive ? withOpacity(PRIMARY_BLUE, 0.12) : withOpacity(PRIMARY_BLUE, 0.07),
                      color: isActive ? PRIMARY_BLUE : SUBTLE,
                      '& .MuiSvgIcon-root': {fontSize: d.icon},
                    }}>
                      {Icon && <Icon />}
                    </Box>
                    {!!s.step && (
                      <Box aria-hidden='true' sx={{
                        position: 'absolute', bottom: -4, right: -4,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 16, height: 16, borderRadius: '50%',
                        bgcolor: isActive ? PRIMARY_BLUE : SUBTLE,
                        color: WHITE, fontFamily: FONT, fontWeight: WEIGHT.bold,
                        // 11px is the panel's micro-type floor (see
                        // disputes-v2/gridSx.js:88). The page's zoom means
                        // anything authored smaller lands under 9px on screen.
                        // The circle went 15px -> 16px so an 11px line box
                        // clears its 1px ring on both sides.
                        fontSize: '11px', lineHeight: 1, border: `1px solid ${WHITE}`,
                      }}>
                        {s.step}
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{
                    fontFamily: FONT, fontSize: SIZE['2xs'],
                    fontWeight: (d.dimInactiveLabel && !isActive) ? WEIGHT.medium : WEIGHT.semibold,
                    color: isActive ? NAVY : SUBTLE, lineHeight: d.lineHeight, whiteSpace: 'nowrap',
                  }}>
                    {itemText(s)}
                  </Typography>
                </Box>
              </React.Fragment>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
})

export default StepRail
